import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Button, DocusignViewer } from '@lucid/lego';
import type { Theme } from '../utils';
import { DOCUMENT_LOCALE_OPTIONS } from './useTestDriveCheckIn';
import type { useTestDriveCheckIn } from './useTestDriveCheckIn';
import styles from './CheckInAction.module.scss';

type CheckInActionProps = {
  checkIn: ReturnType<typeof useTestDriveCheckIn>;
  theme: Theme;
  labels?: Record<string, string>;
};

/**
 * Split-button + locale-menu check-in control, shared by the test drive
 * "details" (view) and "modify" (edit) surfaces via `useTestDriveCheckIn`.
 */
const CheckInAction: React.FC<CheckInActionProps> = ({ checkIn, theme, labels }) => {
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const localeMenuRef = useRef<HTMLDivElement>(null);
  const [isLocaleMenuOpen, setIsLocaleMenuOpen] = useState(false);

  const {
    canCheckIn,
    resolvedCheckInLocale,
    setSelectedCheckInLocale,
    handleCheckIn,
    isCreatingCheckInAgreement,
    docusignUrl,
    docusignModal,
    handleCloseDocusign,
    handleDocusignMessage,
  } = checkIn;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (localeMenuRef.current && !localeMenuRef.current.contains(event.target as Node)) {
        setIsLocaleMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!canCheckIn) return null;

  return (
    <>
      <div
        ref={localeMenuRef}
        className={classnames(styles.checkInActionContainer, {
          [styles.disabled]: isCreatingCheckInAgreement,
        })}
      >
        <Button
          className={styles.checkInMainButton}
          dataTestId="check-in-button"
          gaComponent="prospect-test-drive-check-in"
          handleClick={() => handleCheckIn()}
          disabled={isCreatingCheckInAgreement || !resolvedCheckInLocale}
          type="primary"
          color="neutral"
          theme={invertedTheme}
          isFluid
          size="large"
          label={
            resolvedCheckInLocale
              ? `${labels?.['details.checkIn'] ?? 'Check-In'} (${DOCUMENT_LOCALE_OPTIONS.find((opt) => opt.value === resolvedCheckInLocale)?.label ?? resolvedCheckInLocale})`
              : (labels?.['details.checkIn'] ?? 'Check-In')
          }
        />
        <Button
          className={styles.checkInDropdownToggle}
          dataTestId="check-in-locale-selector"
          gaComponent="prospect-test-drive-check-in-locale-menu"
          handleClick={() => setIsLocaleMenuOpen((prev) => !prev)}
          disabled={isCreatingCheckInAgreement}
          type="primary"
          color="neutral"
          theme={invertedTheme}
          size="large"
          iconRight={{
            category: 'arrows',
            name: isLocaleMenuOpen ? 'chevron-up' : 'chevron-down',
            dataTestId: 'check-in-locale-chevron',
          }}
        />
        {isLocaleMenuOpen && (
          <ul className={styles.checkInMenu} data-testid="check-in-locale-menu">
            <li className={styles.checkInMenuHeader}>
              {labels?.['details.checkInDocumentLocale'] ?? 'Document language'}
            </li>
            {DOCUMENT_LOCALE_OPTIONS.map((option) => {
              const isSelected = option.value === resolvedCheckInLocale;
              return (
                <li
                  key={option.value}
                  className={classnames(styles.checkInMenuItem, { [styles.selected]: isSelected })}
                  data-testid={`check-in-locale-option-${option.value}`}
                  onClick={() => {
                    setSelectedCheckInLocale(option.value);
                    setIsLocaleMenuOpen(false);
                  }}
                >
                  <span>{option.label}</span>
                  {isSelected && <span>✓</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {docusignUrl && (
        <DocusignViewer
          url={docusignUrl}
          dataTestId="check-in-docusign-viewer"
          isOpen={docusignModal.isOpen}
          onClose={handleCloseDocusign}
          onMessage={handleDocusignMessage}
          theme={invertedTheme}
          labels={{
            title: labels?.['details.checkIn'] ?? 'Check-In',
            loading: labels?.['details.checkInLoading'] ?? 'Loading document...',
            error: labels?.['details.checkInViewerError'] ?? 'Failed to load the document.',
          }}
        />
      )}
    </>
  );
};

export default CheckInAction;
